import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import DnsIcon from '@mui/icons-material/Dns';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const CustomTooltip = ({ title, value, children }) => (
  <Tooltip title={value || title} arrow placement="top" disableFocusListener>
    {children}
  </Tooltip>
);

const SmtpTester = () => {
  const [formData, setFormData] = useState({
    host: 'smtp.email.us-ashburn-1.oci.oraclecloud.com',
    port: '587',
    username: 'ocid1.user.oc1...',
    password: '',
    security: 'tls',
    from_email: '',
    to_email: '',
    subject: 'SMTP Test Check',
    body: '<h1>Hello, World!</h1><p>This is a test email with <b>HTML</b> content.</p>',
    body_type: 'html'
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, success, error
  const [logs, setLogs] = useState([]);
  const [errorDetail, setErrorDetail] = useState('');


  // For body preview
  const [previewMode, setPreviewMode] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTest = async () => {
    setLoading(true);
    setStatus('idle');
    setLogs([]);
    setErrorDetail('');

    try {
      const response = await fetch('/api/test-smtp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          port: parseInt(formData.port)
        })
      });

      const data = await response.json();
      setLogs(data.logs || []);

      if (data.status === 'success') {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorDetail(data.detail);
      }

    } catch (err) {
      setStatus('error');
      setErrorDetail(err.message);
      setLogs(prev => [...prev, `Network Error: ${err.message}`]);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>SMTP Diagnostic</Typography>
          <Typography variant="body2" color="text.secondary">Test connectivity, security handshake, and email delivery.</Typography>
        </Box>
        {status === 'success' && <Chip icon={<CheckCircleIcon />} label="Connected & Sent" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />}
        {status === 'error' && <Chip icon={<ErrorIcon />} label="Connection Failed" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />}
      </Box>

      <Grid container spacing={3} direction="column">
        {/* Top Panel: Inputs */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{
            p: 0,
            overflow: 'hidden',
            borderRadius: 3,
            border: theme => `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Server Configuration
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <CustomTooltip title="The hostname of your SMTP server (e.g., smtp.gmail.com)" value={formData.host}>
                    <TextField
                      fullWidth label="SMTP Host" name="host"
                      value={formData.host} onChange={handleChange}
                      variant="outlined"
                      InputProps={{ startAdornment: <InputAdornment position="start"><DnsIcon color="action" sx={{ mr: 1 }} /></InputAdornment> }}
                    />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTooltip title="Common ports: 25, 465 (SSL), 587 (TLS)" value={formData.port}>
                    <TextField
                      fullWidth label="Port" name="port"
                      value={formData.port} onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>#</InputAdornment>
                      }}
                    />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTooltip title="SMTP Username (usually email address)" value={formData.username}>
                    <TextField
                      fullWidth label="Username" name="username"
                      value={formData.username} onChange={handleChange}
                      variant="outlined"
                    />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTooltip title="SMTP Password or App Password" value={formData.password ? "******" : ""}>
                    <TextField
                      fullWidth label="Password" name="password" type="password"
                      value={formData.password} onChange={handleChange}
                      variant="outlined"
                      InputProps={{ startAdornment: <InputAdornment position="start"><VpnKeyIcon color="action" sx={{ mr: 1 }} /></InputAdornment> }}
                    />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12}>
                  <CustomTooltip title="Select the connection security protocol" value={formData.security}>
                    <TextField
                      select fullWidth label="Security Protocol" name="security"
                      value={formData.security} onChange={handleChange}
                      variant="outlined" SelectProps={{ native: true }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SecurityIcon color="action" sx={{ mr: 1 }} /></InputAdornment> }}
                    >
                      <option value="none">None (Insecure)</option>
                      <option value="tls">STARTTLS (Before login)</option>
                      <option value="ssl">SSL/TLS (Implicit)</option>
                    </TextField>
                  </CustomTooltip>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Email Content
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <CustomTooltip title="The email address sending the message" value={formData.from_email}>
                    <TextField fullWidth label="From Email" name="from_email" value={formData.from_email} onChange={handleChange} variant="outlined" />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTooltip title="The recipient email address" value={formData.to_email}>
                    <TextField fullWidth label="To Email" name="to_email" value={formData.to_email} onChange={handleChange} variant="outlined" />
                  </CustomTooltip>
                </Grid>
                <Grid item xs={12}>
                  <CustomTooltip title="Subject line of the test email" value={formData.subject}>
                    <TextField fullWidth label="Subject" name="subject" value={formData.subject} onChange={handleChange} variant="outlined" />
                  </CustomTooltip>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Message Body</Typography>
                    <Box>
                      <Button
                        size="small"
                        startIcon={<CodeIcon />}
                        variant={!previewMode ? "contained" : "text"}
                        onClick={() => setPreviewMode(false)}
                        sx={{ mr: 1 }}
                      >
                        Code
                      </Button>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        variant={previewMode ? "contained" : "text"}
                        onClick={() => setPreviewMode(true)}
                        disabled={formData.body_type !== 'html'}
                      >
                        Preview
                      </Button>
                    </Box>
                  </Box>

                  {!previewMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={formData.body}
                      onChange={handleChange}
                      name="body"
                      variant="outlined"
                      placeholder="Enter email content here..."
                      sx={{ fontFamily: 'monospace', '& .MuiOutlinedInput-root': { fontFamily: '"JetBrains Mono", monospace' } }}
                    />
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        height: 232, // Match rows=8 approx height
                        p: 2,
                        bgcolor: 'white',
                        color: 'black',
                        overflow: 'auto',
                        borderRadius: 1
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: formData.body }} />
                    </Paper>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <CustomTooltip title="Send as plain text or rendered HTML" value={formData.body_type}>
                      <TextField
                        select
                        label="Content Type"
                        name="body_type"
                        value={formData.body_type}
                        onChange={handleChange}
                        variant="outlined"
                        SelectProps={{ native: true }}
                        size="small"
                        sx={{ width: 150 }}
                      >
                        <option value="plain">Plain Text</option>
                        <option value="html">HTML</option>
                      </TextField>
                    </CustomTooltip>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', bgcolor: 'background.default' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleTest}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{
                  minWidth: 180,
                  borderRadius: 2,
                  boxShadow: theme => theme.shadows[4]
                }}
              >
                {loading ? "Testing..." : "Test Connection"}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Bottom Panel: Logs */}
        <Grid item xs={12}>
          <Paper sx={{
            height: '100%',
            maxHeight: 650,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            fontFamily: '"JetBrains Mono", "Fira Code", monospace'
          }}>
            <Box sx={{ p: '12px 16px', borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#161b22' }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f56' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27c93f' }} />
              <Typography variant="caption" sx={{ color: '#8b949e', ml: 1, fontFamily: 'monospace' }}>SMTP TRANSACTION LOG</Typography>
            </Box>

            <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto', fontFamily: '"Fira Code", monospace', fontSize: '0.85rem' }}>
              {logs.length === 0 && (
                <Box sx={{ color: '#484f58', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="caption">Initiate a test to see transaction details</Typography>
                </Box>
              )}
              {logs.map((log, index) => (
                <Box key={index} sx={{ mb: 0.8, display: 'flex' }}>
                  <span style={{ color: '#8b949e', marginRight: 12, minWidth: 70, userSelect: 'none' }}>[{new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]</span>
                  <span style={{ color: '#3fb950', marginRight: 8, userSelect: 'none' }}>{'>'}</span>
                  <span style={{ color: '#c9d1d9', wordBreak: 'break-word', fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>{log}</span>
                </Box>
              ))}
              {status === 'error' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(248, 81, 73, 0.1)', border: '1px solid #f85149', color: '#f85149', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>CONNECTION ERROR</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1 }}>{errorDetail}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SmtpTester;